"use client";

import { useCallback } from "react";
import { CheckCircle2, Circle, ExternalLink } from "lucide-react";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { LoadAllBrands } from "@/store/modules/brands/brands.helpers";
import { useStore } from "react-redux";
import type { iStore } from "@/store/store.model";

export type BrandOnboardingChecklistItem = {
  id: string;
  label: string;
  description: string;
  category: "configuration" | "review";
  isComplete: boolean;
  isReviewed?: boolean;
  relatedSection?: string;
};

export type BrandChecklistSectionId =
  | "brand-overview"
  | "brand-settings"
  | "brand-tax-settings"
  | "quote-page"
  | "quotes-domain"
  | "trustpilot"
  | "email"
  | "footer"
  | "booking-email-config"
  | "booking-confirmation"
  | "payment-reminder"
  | "voucher-emails"
  | "net-profit-calculation"
  | "month-on-month-trade-expenses"
  | "marketing-deals"
  | "campaigns";

const LEGACY_BRAND_CHECKLIST_SECTIONS = new Set([
  "quote-legal",
  "customer-receipt",
  "atol-certificate",
]);

function normalizeBrandChecklistSection(
  section: string
): BrandChecklistSectionId {
  if (LEGACY_BRAND_CHECKLIST_SECTIONS.has(section)) {
    return "brand-settings";
  }
  return section as BrandChecklistSectionId;
}

export type BrandChecklistWorkspaceSectionId =
  | "general"
  | "workspace-checklist"
  | "integrations"
  | "users"
  | "teams-departments"
  | "enquiry-attributes"
  | "booking-attributes"
  | "suppliers"
  | "customer-payment-methods"
  | "supplier-payment-methods"
  | "ledgers"
  | "activity-log";

interface BrandChecklistContentProps {
  brandId: string;
  items: BrandOnboardingChecklistItem[];
  completedCount: number;
  totalCount: number;
  showProgress?: boolean;
  onOpenBrandSection?: (
    brandId: string,
    section: BrandChecklistSectionId
  ) => void;
  onOpenWorkspaceSection?: (section: BrandChecklistWorkspaceSectionId) => void;
  onReviewUpdated?: () => void;
}

export function BrandChecklistContent({
  brandId,
  items,
  completedCount,
  totalCount,
  showProgress = true,
  onOpenBrandSection,
  onOpenWorkspaceSection,
  onReviewUpdated,
}: BrandChecklistContentProps) {
  const store = useStore<iStore>();

  const { doAction: updateChecklistItem, isProcessing: isUpdatingChecklist } =
    useSDKActionWithDeps(
      () => ({
        action:
          (sdk) => (itemId: string, isReviewed: boolean) =>
            sdk.brands.updateOnboardingChecklistItem(brandId, itemId, {
              isReviewed,
            }),
        onSuccess: () => {
          LoadAllBrands(store, true);
          onReviewUpdated?.();
        },
      }),
      [brandId, store, onReviewUpdated]
    );

  const navigateToRelatedSection = useCallback(
    (relatedSection?: string) => {
      if (!relatedSection) return;
      if (relatedSection === "integrations") {
        onOpenWorkspaceSection?.("integrations");
        return;
      }
      onOpenBrandSection?.(
        brandId,
        normalizeBrandChecklistSection(relatedSection)
      );
    },
    [brandId, onOpenBrandSection, onOpenWorkspaceSection]
  );

  const progressPercent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const configurationItems = items.filter(
    (item) => item.category === "configuration"
  );
  const reviewItems = items.filter((item) => item.category === "review");

  const renderItem = (item: BrandOnboardingChecklistItem) => (
    <div
      key={item.id}
      className={cn(
        "flex items-center gap-3 rounded-lg border p-3",
        item.isComplete ? "border-green-200 bg-green-50/50" : "bg-background"
      )}
    >
      <div className="shrink-0">
        {item.isComplete ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : (
          <Circle className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium leading-snug">{item.label}</p>
        <p className="text-sm text-muted-foreground leading-snug">
          {item.description}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center">
        {item.category === "review" && (
          <label className="flex items-center gap-2 text-sm whitespace-nowrap">
            <Checkbox
              checked={item.isReviewed === true}
              disabled={isUpdatingChecklist}
              onCheckedChange={(checked) => {
                updateChecklistItem(item.id, checked === true);
              }}
            />
            Mark as reviewed
          </label>
        )}
        {item.relatedSection && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigateToRelatedSection(item.relatedSection)}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Open settings
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {showProgress && (
        <div className="space-y-2 rounded-lg border p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Onboarding progress</span>
            <span className="text-muted-foreground">
              {completedCount} of {totalCount} complete
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
      )}

      {configurationItems.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Configuration
          </h4>
          <div className="space-y-2">{configurationItems.map(renderItem)}</div>
        </div>
      )}

      {reviewItems.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Review & approve
          </h4>
          <div className="space-y-2">{reviewItems.map(renderItem)}</div>
        </div>
      )}
    </div>
  );
}
